import { Button } from 'antd';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import React, {
  memo,
  useCallback,
  useRef,
  useMemo,
  useState,
  useEffect
} from 'react';
import { useSearchParams } from 'react-router-dom';
import Tour, { ReactourStep } from 'reactour';
import { ReactComponent as ArrowBackSvg } from '@assets/icons/arrow-back.svg';
import { ReactComponent as CloseSvg } from '@assets/icons/cross-xs.svg';
import {
  OnboardingStepsType,
  TourValue
} from '@context/OnboardingProcessProvider';
import {
  createTaskFromCommentSteps,
  routeForApprovalSteps,
  tourPaused,
  tourExitContent,
  tourExitContentMediaViewer,
  uploadAssetSteps,
  createCampaignSteps,
  reviewMediaSteps,
  tourPausedMediaViewer,
  textHighlightSteps,
  reviewAudioVideoSteps,
  announcementMediaViewerSteps
} from '@components/AppTour/Steps';
import {
  preferredSubdomainCookieName,
  redirectToSubdomain
} from '@components/OrganizationBoundary';
import { useIsDemoOrganization } from '@hooks/useIsDemoOrganization';

const HIDE_CONGRATS_ALWAYS = new Set([
  'intro',
  'tour-paused-media-viewer',
  'tour-paused',
  'announcement-media-viewer'
]);

const PAUSED_MEDIA_VIEWER_TYPES = new Set([
  'create-task-from-comment',
  'review-asset',
  'review-audio-video',
  'text-highlight'
]);

const HIDE_CONGRATS_IF_NOT_LAST = new Set([
  'review-asset',
  'create-task-from-comment',
  'route-for-approval'
]);

function getRouteForApprovalArrowClass(currentStep: number) {
  const leftSteps = [0, 4, 5, 6, 8];
  const rightSteps = [1];
  const bottomSteps = [7, 9, 10];
  if (leftSteps.includes(currentStep)) return 'app-tour-arrow-left';
  if (rightSteps.includes(currentStep)) return 'app-tour-arrow-right';
  if (bottomSteps.includes(currentStep)) return 'app-tour-arrow-bottom';
  return 'app-tour-arrow-top';
}

function getCreateTaskFromCommentArrowClass(currentStep: number) {
  const leftSteps = [3];
  const rightSteps = [1, 2, 6];
  if (leftSteps.includes(currentStep)) return 'app-tour-arrow-left';
  if (rightSteps.includes(currentStep)) return 'app-tour-arrow-right';
  return 'app-tour-arrow-top';
}

function getTextHighlightArrowClass(currentStep: number) {
  if (currentStep === 1) return 'app-tour-arrow-left';
  return 'app-tour-arrow-top';
}

const tourExitContents: Partial<Record<OnboardingStepsType, any>> = {
  'create-task-from-comment': tourExitContentMediaViewer,
  'review-asset': tourExitContentMediaViewer
};

const lastStepNextButtonLabels: Record<string, string> = {
  'upload-asset': 'Complete',
  'create-campaign': 'Complete',
  'review-asset': 'Complete',
  'create-task-from-comment': 'Complete',
  'review-audio-video': 'Complete',
  'text-highlight': 'Complete'
};

type AppTourProps = {
  tourData: TourValue | undefined;
  goToStep: number;
  setTourData: (data: TourValue | undefined) => void;
  setGoToStep: (v: number) => void;
  onCompleteStep: ({
    type,
    targetId,
    hideCongrats
  }: {
    type: OnboardingStepsType;
    targetId?: string | null;
    hideCongrats?: boolean;
  }) => void;
};

export default memo(function AppTour({
  tourData,
  goToStep,
  setGoToStep,
  setTourData,
  onCompleteStep
}: AppTourProps) {
  const isDemoOrganization = useIsDemoOrganization();
  const tourRef = useRef<Tour>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isExitTour, setIsExitTour] = useState(false);
  const [isLoadingPrevBtn, setIsLoadingPrevBtn] = useState(false);
  const [isLoadingNextBtn, setIsLoadingNextBtn] = useState(false);

  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const hideCloseButtons = ['tour-paused', 'tour-paused-media-viewer'].includes(
    tourData?.type || ''
  )
    ? false
    : !isExitTour;
  const showPrevButton =
    isExitTour ||
    ([
      'route-for-approval',
      'create-campaign',
      'upload-asset',
      'review-asset',
      'create-task-from-comment',
      'text-highlight',
      'review-audio-video',
      'announcement-media-viewer'
    ].includes(tourData?.type || '') &&
      !!goToStep);
  const showNavigation =
    [
      'route-for-approval',
      'create-campaign',
      'upload-asset',
      'review-asset',
      'create-task-from-comment',
      'text-highlight',
      'review-audio-video',
      'announcement-media-viewer'
    ].includes(tourData?.type || '') &&
    !!goToStep &&
    !isExitTour;

  const arrowClassName = useMemo(() => {
    if (!tourData?.type) return null;
    const stepConditions: Partial<Record<OnboardingStepsType, string>> = {
      'route-for-approval': getRouteForApprovalArrowClass(goToStep),
      'create-campaign':
        goToStep === 1 ? 'app-tour-arrow-left' : 'app-tour-arrow-right',
      'create-task-from-comment': getCreateTaskFromCommentArrowClass(goToStep),
      'review-asset':
        goToStep <= 1 ? 'app-tour-arrow-left' : 'app-tour-arrow-top',
      'upload-asset': 'app-tour-arrow-top',
      'tour-paused-media-viewer': 'app-tour-arrow-top',
      'text-highlight': getTextHighlightArrowClass(goToStep),
      'review-audio-video': 'app-tour-arrow-top',
      'tour-paused': 'app-tour-arrow-right',
      'announcement-media-viewer':
        goToStep === 0 || goToStep === 4
          ? 'app-tour-arrow-left'
          : 'app-tour-arrow-top'
    };
    return stepConditions[tourData.type] || null;
  }, [tourData?.type, goToStep]);

  useEffect(() => {
    if (!tourData?.type) return;
    // enable prev button
    const timeoutId = setTimeout(() => {
      const prevBtn = document.querySelector('[data-tour-elem="left-arrow"]');
      if (prevBtn) prevBtn.removeAttribute('disabled');
    }, 100);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [tourData?.type, goToStep]);

  const steps: ReactourStep[] = useMemo(() => {
    if (!tourData?.type) return [];
    const stepTypes: Partial<Record<OnboardingStepsType, any>> = {
      'create-campaign': createCampaignSteps,
      'upload-asset': uploadAssetSteps,
      'route-for-approval': () =>
        routeForApprovalSteps({
          action: (node) => tourData?.action?.(goToStep, node)
        }),
      'create-task-from-comment': createTaskFromCommentSteps,
      'review-asset': reviewMediaSteps,
      'tour-paused': tourPaused,
      'tour-paused-media-viewer': tourPausedMediaViewer,
      'text-highlight': textHighlightSteps,
      'review-audio-video': reviewAudioVideoSteps,
      'announcement-media-viewer': announcementMediaViewerSteps
    };
    return stepTypes?.[tourData.type]?.() || [];
  }, [setGoToStep, tourData?.type, tourData?.action, goToStep]);

  const maskSpace = useMemo(() => {
    if (
      [
        'review-asset',
        'tour-paused-media-viewer',
        'review-audio-video'
      ].includes(tourData?.type || '')
    )
      return 0;
    if (
      tourData?.type === 'create-task-from-comment' &&
      (goToStep < 2 || goToStep >= 6)
    )
      return 0;

    if (tourData?.type === 'text-highlight') {
      return goToStep === 0 ? 8 : 0;
    }

    return 20;
  }, [tourData?.type, goToStep]);

  const tourSteps = useMemo(() => {
    if (!isExitTour) return steps;
    return steps?.map((item, index) => {
      if (index === goToStep) {
        const exitContent = tourData?.type
          ? tourExitContents?.[tourData.type] || tourExitContent
          : tourExitContent;

        return {
          ...item,
          ...exitContent
        };
      }
      return item;
    });
  }, [steps, isExitTour, tourData?.type, goToStep]);

  const onClose = useCallback(
    (hideCongrats?: boolean) => {
      if (!tourData?.type) return;

      const isNotLastStep = goToStep !== (steps?.length ?? 1) - 1;
      const { type } = tourData;

      const isHideAlways = HIDE_CONGRATS_ALWAYS.has(type);
      const isPausedMediaViewer = PAUSED_MEDIA_VIEWER_TYPES.has(type);
      const isHideIfNotLast = HIDE_CONGRATS_IF_NOT_LAST.has(type);

      if (isPausedMediaViewer && isExitTour) {
        setGoToStep(0);
        setIsExitTour(false);
        setTourData({ type: 'tour-paused-media-viewer' });
        return;
      }

      if (
        (isExitTour || type === 'tour-paused-media-viewer') &&
        isDemoOrganization
      ) {
        redirectToSubdomain(
          Cookies.get(preferredSubdomainCookieName) || 'demo-over',
          'campaigns/all'
        );
        return;
      }

      if (!isHideAlways && isNotLastStep && !isExitTour) {
        setIsExitTour(true);
        return;
      }

      const finalHideCongrats =
        hideCongrats ||
        isExitTour ||
        isHideAlways ||
        (isHideIfNotLast && isNotLastStep);

      setIsExitTour(false);
      setTourData(undefined);

      onCompleteStep({
        type,
        hideCongrats: finalHideCongrats
      });

      tourData.onClose?.(goToStep, isExitTour);

      searchParamsRef.current.delete('startTour');
      setSearchParamsRef.current(searchParamsRef.current);
    },
    [
      isDemoOrganization,
      isExitTour,
      steps,
      goToStep,
      tourData,
      setGoToStep,
      onCompleteStep,
      setIsExitTour,
      setTourData
    ]
  );

  const onPrevButtonClick = async () => {
    if (typeof tourData?.onClickPrev === 'function') {
      try {
        setIsLoadingPrevBtn(true);
        await tourData.onClickPrev(goToStep, isExitTour);
      } finally {
        setIsLoadingPrevBtn(false);
      }
    }
    if (isExitTour) {
      onClose();
      return;
    }
    const prevGoToStep = goToStep - 1;
    setGoToStep(prevGoToStep <= 0 ? 0 : prevGoToStep);
  };

  const prevButton = useMemo(() => {
    const style = {};
    let icon = <ArrowBackSvg />;
    let copy = 'Back';
    if (isExitTour) {
      copy = 'Yes, exit tour';
      icon = <CloseSvg width={20} height={20} />;
    }
    return (
      <Button
        type="text"
        style={style}
        size="small"
        icon={icon}
        loading={isLoadingPrevBtn}
        onClick={onPrevButtonClick}
      >
        {copy}
      </Button>
    );
  }, [tourData?.type, isExitTour, isLoadingPrevBtn, onPrevButtonClick]);

  const onNextButtonClick = async () => {
    if (typeof tourData?.onClickNext === 'function') {
      try {
        setIsLoadingNextBtn(true);
        await tourData.onClickNext(goToStep, isExitTour);
      } finally {
        setIsLoadingNextBtn(false);
      }
    }
    if (isExitTour) {
      setIsExitTour(false);
      return;
    }
    setGoToStep(goToStep + 1);
  };

  const nextButton = useMemo(() => {
    let label = 'Next';
    if (isExitTour) label = 'Continue tour';
    return (
      <Button
        type="default"
        size="small"
        loading={isLoadingNextBtn}
        onClick={onNextButtonClick}
      >
        {label}
      </Button>
    );
  }, [isExitTour, isLoadingNextBtn, onNextButtonClick]);

  const lastStepNextButton = useMemo(() => {
    const label = lastStepNextButtonLabels[tourData?.type || ''] || 'Done';
    return (
      <Button type="default" size="small">
        {label}
      </Button>
    );
  }, [isExitTour, tourData?.type]);

  if (!steps?.length) return null;

  return (
    <Tour
      ref={tourRef}
      className={classNames(
        { 'app-tour-assist': true },
        `app-tour-assist--${tourData?.theme || 'lime-green'}`,
        `app-tour-assist--${tourData?.type}`,
        `app-tour-step--${goToStep}`,
        arrowClassName,
        {
          'b-show-prev-btn': showPrevButton
        }
      )}
      steps={tourSteps}
      goToStep={goToStep}
      isOpen={!!tourData?.type}
      onRequestClose={() => onClose()}
      showNumber={false}
      startAt={0}
      showNavigation={showNavigation}
      closeWithMask={false}
      inViewThreshold={100}
      disableFocusLock={true} // fix input
      disableDotsNavigation={true}
      nextStep={() => null}
      prevStep={() => null}
      prevButton={prevButton}
      nextButton={nextButton}
      lastStepNextButton={lastStepNextButton}
      highlightedMaskClassName={`app-tour-highlighted-mask--${
        tourData?.type
      } b-${tourData?.theme || 'white'}`}
      maskClassName={`app-tour-highlighted-mask app-tour-mask--${tourData?.type}`}
      rounded={8}
      maskSpace={maskSpace}
      showCloseButton={hideCloseButtons}
    />
  );
});
